import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {navigate} from 'BenchlingUtil';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {EditSchemaInterfaceTableQuery} from 'schemas/schema-interface/__generated/EditSchemaInterfaceTableQuery';
import {schemaInterfaceURL} from 'schemas/schema-interface/SchemaInterfaceDAO';
import {schemaFieldsetName} from 'schemas/schema-interface/util/schemaFieldsetName';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {RED} from 'ui-library/Colors';
import TextField from 'ui-library/inputs/TextField';
import Modal from 'ui-library/modals/Modal';
import Spinner from 'ui-library/Spinner';
import {capitalize} from 'util/NameUtil';

type QueryPrefixSource = NonNullable<
  ExtractByTypename<EditSchemaInterfaceTableQuery['tagSchema'], 'TagSchemaNode'>['prefixSource']
>;

export interface RemoveSchemaInterfaceModalProps {
  prefixSource: Pick<QueryPrefixSource, 'id' | 'name' | 'prefix'>;
  onRequestSubmit: (prefix: string) => Promise<void>;
  onRequestClose: () => void;
}

export default function RemoveSchemaInterfaceModal(props: RemoveSchemaInterfaceModalProps): JSX.Element {
  const {
    prefixSource: {id: interfaceId, name: interfaceName, prefix: interfacePrefix},
    onRequestSubmit,
    onRequestClose,
  } = props;
  const [replacementPrefix, setReplacementPrefix] = React.useState<string>('');
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  const interfaceLink = schemaInterfaceURL(interfaceId);
  return (
    <Modal
      enableRedesign={true}
      title={`Remove ${schemaFieldsetName()}`}
      closeable={false}
      onRequestSubmit={async () => {
        setIsSubmitting(true);
        try {
          await onRequestSubmit(replacementPrefix);
        } catch (err) {
          if (!err.responseText?.toLowerCase().includes('prefix')) {
            throw err;
          }
          // Only show the error message within the modal if it's a prefix error
          setErrorMessage(err.responseText);
        } finally {
          setIsSubmitting(false);
        }
      }}
      onRequestClose={onRequestClose}
      scrollable={false}
      buttons={[
        {text: 'Cancel', cancel: true},
        {text: 'Remove', submit: true, style: ButtonStyle.NEGATIVE},
      ]}
      footer={isSubmitting && <Spinner />}
    >
      <p className={css(styles.modalText)}>
        {capitalize(schemaFieldsetName())}{' '}
        <a href={interfaceLink} onClick={navigate}>
          {interfaceName}
        </a>{' '}
        sets a prefix for this schema. Select a <b>new and unique</b> prefix to replace it with.
      </p>
      <div className={css(styles.row)}>
        <div className={css(styles.leftColumn)}>
          <TextField isRequired={false} label="Current prefix" disabled={true} value={interfacePrefix} />
        </div>
        <div className={css(styles.rightColumn)}>
          <TextField
            isRequired={true}
            label="New prefix"
            autoFocus={true}
            onChange={(e) => {
              setReplacementPrefix(e.target.value);
            }}
          />
          {errorMessage && <p className={css(styles.error)}>{errorMessage}</p>}
        </div>
      </div>
    </Modal>
  );
}

export const styles = StyleSheet.create({
  modalText: {
    fontSize: '14px',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
    margin: '5px 0 0',
  },
  leftColumn: {
    flex: 0.5,
    minWidth: 0,
    marginRight: '30px',
  },
  rightColumn: {
    flex: 0.5,
    minWidth: 0,
  },
  error: {
    color: RED,
  },
});
